import React, { useState } from "react";
import { Menu } from "lucide-react";
import { Button } from "../components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "../components/ui/avatar";
import {
  Sheet,
  SheetTrigger,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "../components/ui/sheet";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuGroup,
} from "../components/ui/dropdown-menu"; // ✅ use shadcn/ui version
import { Link, useNavigate } from "react-router-dom";
import { useLogoutUserMutation } from "@/features/api/authApi";
import { toast } from "sonner";
import { useSelector } from "react-redux";
import logo from "@/assets/image/logo/fetc-logo.png";

// Navbar
const Navbar = () => {
  const { user } = useSelector((state) => state.auth);
  const [logoutUser, { data }] = useLogoutUserMutation();
  const navigate = useNavigate();

  const logoutHandler = async () => {
    try {
      await logoutUser().unwrap();
      toast.success(data?.message || "Logged out successfully");
      window.location.href = "/";
    } catch (error) {
      toast.error(error?.data?.message || "Error logging out");
    }
  };

  return (
    <div className="h-20 bg-white dark:bg-[#0A0A0A] text-[#231f40] border-b border-gray-200 dark:border-gray-800 flex items-center md:px-8 shadow-md z-50 relative">
      {/* Logo */}
      <Link to="/" className="flex-shrink-0">
        <img src={logo} alt="FETC Logo" className="h-12 md:h-16" />
      </Link>

      {/* Desktop Menu */}
      <div className="hidden md:flex flex-1 justify-center">
        <MainMenu />
      </div>

      {/* User Actions */}
      <div className="hidden md:flex items-center gap-4 ml-auto">
        {user ? (
          <AvatarMenu user={user} logoutHandler={logoutHandler} />
        ) : (
          <div className="flex gap-2">
            <Link to="/become-partner">
              <Button variant="outline" className="border-2 text-blue-500">
                Become Partner
              </Button>
            </Link>
            <Link to="/online-payment">
              <Button variant="outline" className="border-2 text-blue-500">
                Online Payments
              </Button>
            </Link>
            <Link to="/login">
              <Button variant="outline" className="border-2 text-blue-500">
                My Account
              </Button>
            </Link>
          </div>
        )}
      </div>

      {/* Mobile Menu */}
      <div className="flex md:hidden ml-auto">
        <MobileNavbar user={user} logoutHandler={logoutHandler} />
      </div>
    </div>
  );
};

export default Navbar;

// Avatar Dropdown
const AvatarMenu = ({ user, logoutHandler }) => (
  <DropdownMenu>
    <DropdownMenuTrigger asChild>
      <Avatar className="cursor-pointer">
        <AvatarImage src={user?.photoUrl || "/image/avatar.jpg"} alt="Profile" />
        <AvatarFallback>{user?.name?.charAt(0) || "A"}</AvatarFallback>
      </Avatar>
    </DropdownMenuTrigger>

    <DropdownMenuContent
      align="end"
      className="w-56 mt-2 rounded-md bg-white border shadow-md dark:bg-gray-900 dark:border-gray-700 p-2 z-50"
    >
      <DropdownMenuGroup>
        <DropdownMenuItem asChild>
          <Link
            to="/dashboard/profile"
            className="block w-full px-3 py-2 rounded-md hover:bg-gray-100 dark:hover:bg-gray-800"
          >
            Profile
          </Link>
        </DropdownMenuItem>

        {user?.role === "admin" && (
          <DropdownMenuItem asChild>
            <Link
              to="/admin/dashboard"
              className="block w-full px-3 py-2 rounded-md hover:bg-gray-100 dark:hover:bg-gray-800"
            >
              Master Panel
            </Link>
          </DropdownMenuItem>
        )}

        {user?.role === "instructor" && (
          <DropdownMenuItem asChild>
            <Link
              to="/admin/newsflash"
              className="block w-full px-3 py-2 rounded-md hover:bg-gray-100 dark:hover:bg-gray-800"
            >
              CRM Panel
            </Link>
          </DropdownMenuItem>
        )}
      </DropdownMenuGroup>

      <DropdownMenuSeparator />

      <DropdownMenuItem
        onClick={logoutHandler}
        className="text-red-600 px-3 py-2 rounded-md hover:bg-red-50 dark:hover:bg-red-900"
      >
        Logout
      </DropdownMenuItem>
    </DropdownMenuContent>
  </DropdownMenu>
);

// Mobile Navbar
const MobileNavbar = ({ user, logoutHandler }) => (
  <Sheet>
    <SheetTrigger asChild>
      <Button
        size="icon"
        variant="outline"
        className="rounded-full bg-gray-100 hover:bg-gray-200"
      >
        <Menu />
      </Button>
    </SheetTrigger>

    <SheetContent side="right" className="w-[80%] sm:w-96 z-50">
      <div className="flex flex-col h-full p-4">
        {/* Logo */}
        <SheetHeader className="mb-4">
          <SheetTitle>
            <img src={logo} alt="FETC Logo" className="h-12" />
          </SheetTitle>
        </SheetHeader>

        {/* Menu */}
        <div className="flex-1">
          <MainMenu isMobile />
        </div>

        {/* Auth Buttons */}
        <div className="mt-6 flex flex-col gap-3 items-start">
          {user ? (
            <>
              <Link to="/dashboard/profile" className="text-blue-600 hover:text-blue-800">
                Profile
              </Link>
              {user?.role === "admin" && (
                <Link to="/admin/dashboard" className="text-blue-600 hover:text-blue-800">
                  Master Panel
                </Link>
              )}
              {user?.role === "instructor" && (
                <Link to="/admin/newsflash" className="text-blue-600 hover:text-blue-800">
                  CRM Panel
                </Link>
              )}
              <Button
                variant="outline"
                className="text-red-500 mt-2 w-full"
                onClick={logoutHandler}
              >
                Logout
              </Button>
            </>
          ) : (
            <>
              <Link to="/become-partner" className="w-full">
                <Button variant="outline" className="w-full text-blue-500">
                  Become Partner
                </Button>
              </Link>
              <Link to="/online-payment" className="w-full">
                <Button variant="outline" className="w-full text-blue-500">
                  Online Payments
                </Button>
              </Link>
              <Link to="/login" className="w-full">
                <Button variant="outline" className="w-full text-blue-500">
                  Login
                </Button>
              </Link>
            </>
          )}
        </div>
      </div>
    </SheetContent>
  </Sheet>
);

// Main Menu
const MainMenu = ({ isMobile = false }) => (
  <ul className={`flex ${isMobile ? "flex-col gap-4" : "space-x-6"} text-blue-500`}>
    <NavItem to="/" label="Home" />
    {/* <NavItem to="/work-permit" label="Work Permit" /> */}
    <NavItem to="/career-assessment" label="Career Assessment" />
    <DropdownNav
      label="Book an Exam"
      items={[
        { label: "UKVI - SELT", link: "/selt" },
        { label: "ETS - TOEFL", link: "/toefl" },
        { label: "IDP - IELTS", link: "/ielts" },
        { label: "PTE", link: "/pte" },
        { label: "GRE/GMAT", link: "/gre" },
        { label: "SAT", link: "/sat" },
      ]}
    />
    <DropdownNav
      label="Training"
      items={[
        { label: "Mock", link: "/mock" },
        { label: "Online", link: "/online" },
        { label: "Offline", link: "/mock" },
      ]}
    />
    <DropdownNav
      label="Study Abroad"
      items={[
        { label: "United States", link: "/study-abroad/united-states" },
        { label: "Australia", link: "/study-abroad/australia" },
        { label: "United Kingdom", link: "/study-abroad/united-kingdom" },
        { label: "Ireland", link: "/study-abroad/ireland" },
        { label: "Europe", link: "/study-abroad/europe" },
        { label: "New Zealand", link: "/study-abroad/new-zealand" },
        { label: "Dubai", link: "/study-abroad/dubai" },

      ]}
    />
    <NavItem to="/blog" label="Blogs & News" />
  </ul>
);

// NavItem
const NavItem = ({ to, label }) => (
  <li>
    <Link
      to={to}
      className="hover:bg-blue-100 px-3 py-2 rounded-md block transition-all"
    >
      {label}
    </Link>
  </li>
);

// Dropdown Nav
const DropdownNav = ({ label, items }) => {
  const [open, setOpen] = useState(false);

  return (
    <DropdownMenu open={open} onOpenChange={setOpen} modal={false}>
      <DropdownMenuTrigger asChild>
        <button className="flex items-center gap-1 cursor-pointer">
          {label} {open ? "↑" : "↓"}
        </button>
      </DropdownMenuTrigger>

      <DropdownMenuContent className="bg-white shadow-lg rounded-md mt-2 p-2 border dark:bg-gray-900 z-50">
        {items.map((item, idx) => (
          <DropdownMenuItem key={idx} asChild>
            <Link
              to={item.link}
              className="block w-full hover:bg-blue-100 px-2 py-2 rounded-md text-gray-700"
            >
              {item.label}
            </Link>
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
